/**
 * Standardizes a raw zone name according to predefined rules, aiming to replicate
 * the logic of the provided Python `normalize_zone_name` function.
 * The primary goal is to convert various zone name formats, especially those
 * involving airport codes, to a standard format like `XXX_N` or just `N` for simple zones.
 * @param rawZoneName - The raw zone name from the Excel sheet.
 * @returns The standardized zone name.
 */
export const standardizeZoneName = (
  rawZoneName: string | number | undefined
): string => {
  if (rawZoneName === null || rawZoneName === undefined) {
    return ''
  }
  const zoneName = String(rawZoneName).trim()

  if (zoneName === '') {
    return ''
  }
  // Case 1: Already in format XXX_N (e.g., LAX_1)
  if (/^[A-Z]{3}_\d+$/i.test(zoneName)) {
    // Return the standardized format with uppercase airport code
    const match = zoneName.match(/^([A-Za-z]{3})_(\d+)$/i)
    if (match) {
      return `${match[1].toUpperCase()}_${match[2]}`
    }
    return zoneName
  }

  // Case 2: Format like XXXN (e.g., LAX1, DFW3)
  let match = zoneName.match(/^([A-Za-z]{3})(\d+)$/i)
  if (match) {
    return `${match[1].toUpperCase()}_${match[2]}`
  }

  // Case 3: Format like XXX-ZoneN (e.g., ATL-Zone2, JFK-Zone3)
  match = zoneName.match(/^([a-zA-Z]{3})[-_\s]*[Zz][Oo][Nn][Ee][-_\s]*(\d+)$/i)
  if (match) {
    return `${match[1].toUpperCase()}_${match[2]}`
  }

  // Case 4: Format like XXX Zone N (with space) (e.g., JFK Zone 3)
  match = zoneName.match(/^([a-zA-Z]{3})\s+[Zz][Oo][Nn][Ee]\s+(\d+)$/i)
  if (match) {
    return `${match[1].toUpperCase()}_${match[2]}`
  }

  // Case 5: Format like XXX N (with space, no "Zone") (e.g., JFK 3)
  match = zoneName.match(/^([a-zA-Z]{3})\s+(\d+)$/i)
  if (match) {
    return `${match[1].toUpperCase()}_${match[2]}`
  }

  // Case 6: Format like "ZoneN", "Zone N", "zoneN", or "zone N" (case-insensitive Zone)
  match = zoneName.match(/^[Zz][Oo][Nn][Ee][-_\s]*(\d+)$/i)
  if (match) {
    return match[1] // Return just the digit
  }
  return zoneName
}

/**
 * Normalizes a header string for comparison by removing parentheses content,
 * all whitespace, and converting to lowercase.
 * @param header - The header string to normalize.
 * @returns The normalized header string.
 */
export const normalizeHeaderForComparison = (
  header: string | null | undefined
): string => {
  if (typeof header !== 'string' || !header) return ''
  return header
    .replace(/\(.*\)/g, '') // Remove content within English parentheses
    .replace(/（.*?）/g, '') // Remove content within Chinese parentheses
    .replace(/\s+/g, '') // Remove ALL whitespace (spaces, newlines, tabs)
    .toLowerCase()
}
